import React from 'react';
import {
  Alert,
  BusinessIcon,
  Button,
  Divider,
  Field,
  Input,
  PersonOutlineIcon,
  Spinner,
  Text,
} from '@billda/design-system';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { usePermissions } from '@billda/permissions-ui';
import { RHFNinoInput } from '../../../shared/components/forms/RHFNinoInput';
import { getErrorMessage } from '@billda/shared-ui';
import {
  businessDetailsCaptureSchema,
  BusinessDetailsCaptureFormData,
  defaultBusinessDetailsValues,
  normalizeUtr,
  normalizeCrn,
} from '../types/businessDetailsCaptureSchema';
import { useBusinessDetailsCapture } from '../hooks/useBusinessDetailsCapture';
import { UpsertBusinessDetailsRequest } from '../api/businessDetailsApi';

interface BusinessDetailsCaptureFormProps {
  organisationName: string;
  onSuccess: () => void;
  onError: (message: string) => void;
}

const BusinessDetailsCaptureForm: React.FC<BusinessDetailsCaptureFormProps> = ({
  organisationName,
  onSuccess,
  onError,
}) => {
  const permissions = usePermissions();
  const canSkip = permissions.includes('users.invite');

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<BusinessDetailsCaptureFormData>({
    defaultValues: defaultBusinessDetailsValues,
    resolver: yupResolver(businessDetailsCaptureSchema) as any,
    mode: 'onChange',
  });

  const businessType = watch('businessType');

  const { mutate: saveBusinessDetails, isPending } =
    useBusinessDetailsCapture();

  const mutateCallbacks = {
    onSuccess: () => {
      onSuccess();
    },
    onError: (error: Error) => {
      onError(getErrorMessage(error, 'Failed to save business details'));
    },
  };

  const handleSkip = () => {
    // When skipping, save with isBookingWork = false
    saveBusinessDetails(
      {
        isBookingWork: false,
      },
      mutateCallbacks
    );
  };

  const onSubmit = (data: BusinessDetailsCaptureFormData) => {
    const request: UpsertBusinessDetailsRequest = {
      isBookingWork: true,
      businessName: data.businessName || undefined,
    };

    if (data.businessType === 'soleTrader') {
      request.soleTrader = {
        hmrcFullName: data.soleTrader.hmrcFullName,
        utr: data.soleTrader.utr,
        nationalInsuranceNumber:
          data.soleTrader.nationalInsuranceNumber.toUpperCase(),
      };
    } else {
      request.limitedCompany = {
        companyName: data.limitedCompany.companyName,
        utr: data.limitedCompany.utr,
        companyRegistrationNumber:
          data.limitedCompany.companyRegistrationNumber.toUpperCase(),
      };
    }

    saveBusinessDetails(request, mutateCallbacks);
  };

  return (
    <div className="w-full">
      <div className="mb-8 text-center">
        <Text
          as="h1"
          variant="h4"
          className="mb-2 font-semibold text-content-primary"
          data-testid="business-details-title"
        >
          Business Details
        </Text>
        <Text as="div" variant="body1" color="secondary">
          Provide your business information to complete your profile for{' '}
          {organisationName}
        </Text>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-6">
          {/* Business Type Selection */}
          <div>
            <Text as="div" variant="body2" className="mb-2 font-medium">
              Business Type
            </Text>
            <Controller
              name="businessType"
              control={control}
              render={({ field }) => (
                <div
                  className="flex w-full gap-2"
                  data-testid="business-type-toggle"
                >
                  <Button
                    type="button"
                    variant={
                      field.value === 'soleTrader' ? 'primary' : 'outline'
                    }
                    fullWidth
                    onClick={() => field.onChange('soleTrader')}
                    data-testid="sole-trader-btn"
                  >
                    <PersonOutlineIcon aria-hidden="true" className="size-4" />
                    Sole Trader
                  </Button>
                  <Button
                    type="button"
                    variant={
                      field.value === 'limitedCompany' ? 'primary' : 'outline'
                    }
                    fullWidth
                    onClick={() => field.onChange('limitedCompany')}
                    data-testid="limited-company-btn"
                  >
                    <BusinessIcon aria-hidden="true" className="size-4" />
                    Limited Company
                  </Button>
                </div>
              )}
            />
            {errors.businessType && (
              <Text
                as="div"
                variant="caption"
                className="mt-2 text-content-error"
              >
                {errors.businessType.message}
              </Text>
            )}
          </div>

          {/* Business Name (optional) */}
          <Controller
            name="businessName"
            control={control}
            render={({ field, fieldState }) => (
              <Field
                label="Trading Name (Optional)"
                error={fieldState.error?.message}
              >
                <Input
                  {...field}
                  invalid={!!fieldState.error}
                  disabled={isPending}
                  data-testid="business-name-input"
                />
              </Field>
            )}
          />

          <Divider />

          {/* Sole Trader Fields */}
          {businessType === 'soleTrader' && (
            <>
              <Text as="div" variant="body1" className="font-medium">
                CIS Details - Sole Trader
              </Text>

              <Controller
                name="soleTrader.hmrcFullName"
                control={control}
                render={({ field, fieldState }) => (
                  <Field
                    label="Full Name (as registered with HMRC)"
                    error={fieldState.error?.message}
                  >
                    <Input
                      {...field}
                      invalid={!!fieldState.error}
                      disabled={isPending}
                      data-testid="hmrc-full-name-input"
                    />
                  </Field>
                )}
              />

              <Controller
                name="soleTrader.utr"
                control={control}
                render={({ field, fieldState }) => (
                  <Field
                    label="Unique Taxpayer Reference (UTR)"
                    error={fieldState.error?.message}
                    hint="10 digits - found on your Self Assessment tax return (SA250)"
                  >
                    <Input
                      {...field}
                      onChange={e =>
                        field.onChange(normalizeUtr(e.target.value))
                      }
                      invalid={!!fieldState.error}
                      disabled={isPending}
                      maxLength={11}
                      data-testid="sole-trader-utr-input"
                    />
                  </Field>
                )}
              />

              <RHFNinoInput
                name="soleTrader.nationalInsuranceNumber"
                control={control}
                label="National Insurance Number"
                fullWidth
                helperText="Format: AB 12 34 56 C - found on your payslip, P60, or NI card"
                disabled={isPending}
                data-testid="nino-input"
              />
            </>
          )}

          {/* Limited Company Fields */}
          {businessType === 'limitedCompany' && (
            <>
              <Text as="div" variant="body1" className="font-medium">
                CIS Details - Limited Company
              </Text>

              <Controller
                name="limitedCompany.companyName"
                control={control}
                render={({ field, fieldState }) => (
                  <Field
                    label="Company Name (as registered)"
                    error={fieldState.error?.message}
                  >
                    <Input
                      {...field}
                      invalid={!!fieldState.error}
                      disabled={isPending}
                      data-testid="company-name-input"
                    />
                  </Field>
                )}
              />

              <Controller
                name="limitedCompany.utr"
                control={control}
                render={({ field, fieldState }) => (
                  <Field
                    label="Company UTR"
                    error={fieldState.error?.message}
                    hint="10 digits - found on your Corporation Tax letter (CT41G)"
                  >
                    <Input
                      {...field}
                      onChange={e =>
                        field.onChange(normalizeUtr(e.target.value))
                      }
                      invalid={!!fieldState.error}
                      disabled={isPending}
                      maxLength={11}
                      data-testid="company-utr-input"
                    />
                  </Field>
                )}
              />

              <Controller
                name="limitedCompany.companyRegistrationNumber"
                control={control}
                render={({ field, fieldState }) => (
                  <Field
                    label="Company Registration Number (CRN)"
                    error={fieldState.error?.message}
                    hint="8 characters - found on your certificate of incorporation"
                  >
                    <Input
                      {...field}
                      onChange={e =>
                        field.onChange(normalizeCrn(e.target.value))
                      }
                      invalid={!!fieldState.error}
                      disabled={isPending}
                      maxLength={8}
                      className="uppercase"
                      data-testid="crn-input"
                    />
                  </Field>
                )}
              />
            </>
          )}

          <Alert severity="info" className="mt-4">
            This information is required for CIS (Construction Industry Scheme)
            compliance and will be verified by the organisation.
          </Alert>

          <div className="flex flex-col justify-between gap-4 sm:flex-row">
            {canSkip ? (
              <Button
                variant="outline"
                onClick={handleSkip}
                disabled={isPending}
                data-testid="skip-business-details-button"
              >
                I am not booking in work - Skip
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={onSuccess}
                disabled={isPending}
                data-testid="skip-for-now-button"
              >
                Skip for now
              </Button>
            )}
            <div className="grow" />
            <Button
              variant="neutral"
              type="submit"
              disabled={!isValid || isPending}
              data-testid="submit-business-details-button"
            >
              {isPending ? (
                <>
                  <Spinner size={20} className="mr-2" />
                  Saving...
                </>
              ) : (
                'Save and Continue'
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default BusinessDetailsCaptureForm;
